/**
 * KlineChartPanel
 * K线行情看板组件
 * 展示指定交易对的K线数据，支持切换时间周期和技术指标
 * 集成 LightweightCandlestickChart 实现完整的 OHLC 和技术指标展示
 *
 * @author page
 * @date 2026-04-21
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {App, Card, InputNumber, Modal, Spin} from 'antd';
import {simulateTradingService} from '../../services/simulateService';
import type {IndicatorConfigMap, IndicatorInstanceConfig, KlineData} from '../../types/simulate';
import {getNextIndicatorColor, INDICATOR_PALETTES} from '../../types/simulate';
import type {KLineBar} from '../charts/LightweightCandlestickChart';
import LightweightCandlestickChart from '../charts/LightweightCandlestickChart';
import {useSidebarState} from '../../contexts/SidebarContext';

/** localStorage 存储键前缀 */
const STORAGE_PREFIX = 'simulate_kline_indicators_';

/** 获取品种对应的存储键 */
export const getStorageKey = (pair: string): string => `${STORAGE_PREFIX}${pair.replace(/[/\\]/g, '_')}`;

/** 默认指标实例配置 */
export const getDefaultInstances = (): IndicatorConfigMap => ({
    'EMA': [{enabled: true, periods: [7, 25], color: '#4d9fff'}],
    'BOLL': [{enabled: false, periods: [20], stdDev: 2, color: '#f5222d'}],
    'MACD': [{enabled: false, periods: [12, 26, 9], color: '#fa541c'}],
    'RSI': [{enabled: false, periods: [14], color: '#9254de'}],
    'KDJ': [{enabled: false, periods: [9], color: '#f5a623'}],
    'CCI': [{enabled: false, periods: [14], color: '#13c2c2'}],
    'ATR': [{enabled: false, periods: [14], color: '#f5a623'}],
    'OBV': [{enabled: false, periods: [14], color: '#2f54eb'}],
    'ADX': [{enabled: false, periods: [14], color: '#f5222d'}],
});

/** 各指标类型的默认实例模板 */
export const getDefaultInstanceForType = (type: string): any => {
    const map: Record<string, any> = {
        'EMA': {enabled: true, periods: [7, 25], color: '#4d9fff'},
        'SMA': {enabled: true, periods: [7, 25], color: '#eb2f96'},
        'WMA': {enabled: true, periods: [7, 25], color: 'var(--pt-bull)'},
        'BOLL': {enabled: true, periods: [20], stdDev: 2, color: '#f5222d'},
        'MACD': {enabled: true, periods: [12, 26, 9], color: '#fa541c'},
        'RSI': {enabled: true, periods: [14], color: '#9254de'},
        'KDJ': {enabled: true, periods: [9], color: '#f5a623'},
        'CCI': {enabled: true, periods: [14], color: '#13c2c2'},
        'ATR': {enabled: true, periods: [14], color: '#f5a623'},
        'OBV': {enabled: true, periods: [14], color: '#2f54eb'},
        'ADX': {enabled: true, periods: [14], color: '#f5222d'},
    };
    return map[type] || {enabled: true, periods: [14], color: 'var(--pt-primary)'};
};

/** 数组去重（保持原顺序） */
const uniqueArray = <T, >(arr: T[]): T[] => {
    const result: T[] = [];
    arr.forEach(item => {
        if (result.indexOf(item) === -1) result.push(item);
    });
    return result;
};

const normalizeNumber = (value: unknown): number | undefined => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'string' && value.trim() === '') return undefined;
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return undefined;
    return num;
};

/** 时间帧到秒数的映射 */
const TIMEFRAME_SECONDS: Record<string, number> = {
    '1m': 60,
    '5m': 300,
    '1h': 3600,
    '4h': 14400,
    '1d': 86400,
};

/** 对实例配置中的 periods 去重 */
export const dedupInstancePeriods = (inst: any): any => {
    if (!inst) return inst;
    const periods = inst.periods ? uniqueArray(inst.periods) : [];
    return {...inst, periods};
};

/** 为缺失颜色的实例补上自动分配的颜色 */
export const fillMissingColors = (config: IndicatorConfigMap): IndicatorConfigMap => {
    const result: IndicatorConfigMap = {};
    Object.entries(config).forEach(([type, instances]) => {
        result[type] = (instances || []).map(inst => {
            if (inst.color) return inst;
            const existingColors = (instances || [])
                .filter((i: IndicatorInstanceConfig) => i.color)
                .map((i: IndicatorInstanceConfig) => i.color!);
            return {...inst, color: getNextIndicatorColor(type, existingColors)};
        });
    });
    return result;
};

/** OHLC 数据接口 */
export interface OhlcData {
    latestKline: {
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number;
    };
    priceChange: number;
    priceChangePercent: number;
}

/**
 * KlineChartPanel 组件属性接口
 */
interface KlineChartPanelProps {
    /** 当前选中的交易对 */
    tradingPair: string;
    /** 当前交易模式 */
    productType?: 'spot' | 'contract';
    /** 可选的交易对列表 */
    availablePairs?: string[];
    /** 交易对切换回调 */
    onPairChange?: (pair: string) => void;
    /** OHLC 数据变化回调 */
    onOhlcDataChange?: (ohlcData: OhlcData | null) => void;
    /** 当前时间周期（由 SimulatePage 管理） */
    timeframe: string;
    /** 指标实例配置（由 SimulatePage 管理） */
    indicatorInstances: IndicatorConfigMap;
    /** 指标实例配置变化回调 */
    onIndicatorInstancesChange: (instances: IndicatorConfigMap) => void;
}


/**
 * KlineChartPanel - K线行情看板组件
 * 展示指定交易对的K线数据，支持切换时间周期和技术指标。
 * 集成 LightweightCandlestickChart 实现完整的 OHLC 和技术指标展示。
 *
 * @author page
 * @date 2026-04-23
 */
const KlineChartPanel: React.FC<KlineChartPanelProps> = ({
                                                             tradingPair,
                                                             productType,
                                                             availablePairs = [],
                                                             onPairChange,
                                                             onOhlcDataChange,
                                                             timeframe,
                                                             indicatorInstances,
                                                             onIndicatorInstancesChange,
                                                         }) => {
    const {message: messageApi} = App.useApp();
    const {collapsed} = useSidebarState();
    /** K线数据列表 */
    const [klineData, setKlineData] = useState<KlineData[]>([]);
    /** 加载状态 */
    const [loading, setLoading] = useState(false);
    /** 指标配置弹窗显示状态 */
    const [showIndicatorModal, setShowIndicatorModal] = useState(false);
    /** 编辑中的指标副本（弹窗内编辑用） */
    const [draftInstances, setDraftInstances] = useState<IndicatorConfigMap>({});
    /** 当前品种 ref，供 loadData 使用，避免 tradingPair 变化过早触发请求 */
    const tradingPairRef = useRef(tradingPair);
    /** 实时价格水平线数据 */
    const [markPrice, setMarkPrice] = useState<number | null>(null);
    /** 页面打开时间戳，用于计算定时刷新的随机偏移 */
    const pageOpenModRef = useRef(Math.floor(Date.now() / 1000) % 10);
    /** 上次 K 线刷新时间戳，用于越界提前刷新判断 */
    const lastKlineRefreshRef = useRef(0);
    /** 可见指标映射 */
    const visibleIndicators = useMemo(() => {
        const result: Record<string, boolean> = {'Volume': true};
        Object.entries(indicatorInstances).forEach(([type, instances]) => {
            instances.forEach(inst => {
                if (!inst.enabled) return;
                if ('MACD' === type) {
                    result[`MACD_${inst.periods.join('_')}`] = true;
                } else if ('BOLL' === type) {
                    result[`BOLL_${inst.periods[0]}`] = true;
                } else {
                    inst.periods.forEach(p => {
                        result[`${type}_${p}`] = true;
                    });
                }
            });
        });
        return result;
    }, [indicatorInstances]);
    /** 指标颜色映射：{ 'EMA_7': '#4d9fff', 'EMA_25': '#00b4d8', ... } */
    const indicatorColorMap = useMemo(() => {
        const FALLBACK_PALETTE = ['var(--pt-primary)', 'var(--pt-bull)', 'var(--pt-bull)', '#f5a623'];
        const map: Record<string, string> = {};
        Object.entries(indicatorInstances).forEach(([type, instances]) => {
            instances.forEach(inst => {
                if (!inst.enabled || !inst.color) return;
                const palette = INDICATOR_PALETTES[type] || FALLBACK_PALETTE;
                const baseIdx = Math.max(0, palette.indexOf(inst.color));
                if ('MACD' === type) {
                    map[`MACD_${inst.periods.join('_')}`] = inst.color;
                } else if ('BOLL' === type) {
                    map[`BOLL_${inst.periods[0]}`] = inst.color;
                } else {
                    // 每个周期取类型调色板中不同的颜色，从实例色索引开始偏移
                    inst.periods.forEach((p, pi) => {
                        map[`${type}_${p}`] = palette[(baseIdx + pi) % palette.length];
                    });
                }
            });
        });
        return map;
    }, [indicatorInstances]);
    /** 指标周期标签映射：BOLL 需要合并 stdDev */
    const indicatorPeriodLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        Object.entries(indicatorInstances).forEach(([type, instances]) => {
            instances.forEach(inst => {
                if (!inst.enabled) return;
                if ('BOLL' === type) {
                    labels[`BOLL_${inst.periods[0]}`] = `${inst.periods[0]},${inst.stdDev ?? 2}`;
                }
            });
        });
        return labels;
    }, [indicatorInstances]);

    /** 原始指标数据 */
    const [rawIndicators, setRawIndicators] = useState<any>(null);
    /** 容器引用 */
    const chartContainerRef = useRef<HTMLDivElement>(null);
    /** 指标计算防抖 */
    const loadingRef = useRef(false);
    /** 图表重建版本号 */
    const [chartRenderKey, setChartRenderKey] = useState(0);

    /**
     * 加载K线数据和技术指标
     * 根据交易对和时间周期从后端获取K线数据和技术指标
     */
    const loadData = useCallback(async (silent = false) => {
        if (loadingRef.current) return;
        const currentPair = tradingPairRef.current;

        try {
            loadingRef.current = true;
            if (!silent) setLoading(true);

            // 从 indicatorInstances 构建需要请求的指标
            const indicatorTypes = new Set<string>();
            const periodParams: Record<string, any> = {};

            Object.entries(indicatorInstances).forEach(([type, instances]) => {
                instances.forEach(inst => {
                    if (!inst.enabled) return;
                    indicatorTypes.add(type);

                    if ('BOLL' === type) {
                        // BOLL：每个实例的 period 和 stdDev 分别入列表
                        if (!periodParams.bollPeriods) periodParams.bollPeriods = [];
                        if (!periodParams.bollStdDevs) periodParams.bollStdDevs = [];
                        periodParams.bollPeriods.push(inst.periods[0]);
                        periodParams.bollStdDevs.push(inst.stdDev ?? 2);
                    } else if ('MACD' === type) {
                        // MACD：各实例编码为 "fast,slow,signal" 用分号拼接
                        if (!periodParams.macdParams) periodParams.macdParams = '';
                        else periodParams.macdParams += ';';
                        periodParams.macdParams += uniqueArray(inst.periods).join(',');
                    } else {
                        // 其他指标：收集去重后的周期值
                        const periodsKey = `${type.toLowerCase()}Periods`;
                        if (!periodParams[periodsKey]) periodParams[periodsKey] = [];
                        uniqueArray(inst.periods).forEach(p => {
                            if (!periodParams[periodsKey].includes(p)) {
                                periodParams[periodsKey].push(p);
                            }
                        });
                    }
                });
            });

            const indicatorsToRequest = Array.from(indicatorTypes).join(',');

            const res = await simulateTradingService.getKlineWithIndicators(
                currentPair,
                timeframe,
                100,
                indicatorsToRequest || undefined,
                productType,
                Object.keys(periodParams).length > 0 ? periodParams : undefined
            );

            const responseData = res.data?.data;
            if (responseData) {
                // 设置K线数据
                const kline = Array.isArray(responseData.kline) ? responseData.kline : [];
                setKlineData(kline);

                // 设置指标数据
                if (responseData.indicators) {
                    setRawIndicators(responseData.indicators);
                } else {
                    setRawIndicators(null);
                }
            }
        } catch (error: any) {
            if (!silent) {
                messageApi.error(error.response?.data?.message || error.message || '获取K线数据失败');
            }
        } finally {
            lastKlineRefreshRef.current = Date.now();
            if (!silent) setLoading(false);
            loadingRef.current = false;
        }
    }, [timeframe, indicatorInstances, productType]);

    // K 线数据和 loadData 的 ref，供 ticker 使用（避免闭包过期和定时器重启）
    const klineDataRef = useRef<KlineData[]>([]);
    klineDataRef.current = klineData;
    const loadDataRef = useRef(loadData);
    loadDataRef.current = loadData;

    // 同步 tradingPair 到 ref（必须在 loadData effect 之前，确保取到的是最新品种）
    useEffect(() => {
        tradingPairRef.current = tradingPair;
    }, [tradingPair]);

    // 当指标配置或时间周期变化时重新加载数据
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    // 侧边栏伸缩会触发宽度动画，动画结束后重建图表实例，避免 canvas 假死空白
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setChartRenderKey(prev => prev + 1);
        }, 260);

        return () => window.clearTimeout(timer);
    }, [collapsed]);

    // K 线定时静默刷新：按时间帧 + 10 + 随机秒数 间隔刷新
    useEffect(() => {
        const seconds = (TIMEFRAME_SECONDS[timeframe] || 3600) + 10 + pageOpenModRef.current;
        const timer = setInterval(() => loadData(true), seconds * 1000);
        return () => clearInterval(timer);
    }, [timeframe, loadData]);

    // 实时价格轮询：固定 30 + 随机秒数 间隔获取实时价格
    useEffect(() => {
        let active = true;
        const fetchPrice = async () => {
            try {
                const res = await simulateTradingService.getRealtimePrice(
                    tradingPairRef.current, productType);
                if (!active) return;
                const data = res.data?.data;
                if (data?.lastPrice != null) {
                    const price = Number(data.lastPrice);
                    if (isFinite(price)) {
                        setMarkPrice(price);
                        // 越界提前刷新：价格超出最后一根 K 线范围且距下次定时刷新超过 2 分钟
                        const kd = klineDataRef.current;
                        if (kd.length > 0) {
                            const lastBar = kd[kd.length - 1];
                            if (price > lastBar.high || price < lastBar.low) {
                                const refreshInterval = ((TIMEFRAME_SECONDS[timeframe] || 3600) + 10 + pageOpenModRef.current) * 1000;
                                const nextRefreshTime = lastKlineRefreshRef.current + refreshInterval;
                                const timeUntilNext = nextRefreshTime - Date.now();
                                if (timeUntilNext > 120000) {
                                    loadDataRef.current(true);
                                }
                            }
                        }
                    }
                }
            } catch {
                // 静默失败，不影响用户体验
            }
        };
        fetchPrice(); // 立即执行一次
        const intervalMs = (30 + pageOpenModRef.current) * 1000;
        const timer = setInterval(fetchPrice, intervalMs);
        return () => {
            active = false;
            clearInterval(timer);
        };
    }, [tradingPair, productType]);


    /**
     * 将后端指标数据转换为前端 LightweightCandlestickChart 所需的格式
     */
    const transformIndicators = useCallback((): Record<string, any[]> | undefined => {
        if (!rawIndicators) return undefined;

        const result: Record<string, any[]> = {};

        Object.entries(rawIndicators).forEach(([indicatorType, indicatorList]: [string, any]) => {
            if (!Array.isArray(indicatorList)) return;

            indicatorList.forEach((indicator: any) => {
                const period = indicator.period;
                const values = indicator.values || [];

                if ('EMA' === indicatorType) {
                    // EMA: 格式 EMA_ema_{period}
                    const key = `EMA_ema_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('SMA' === indicatorType) {
                    // SMA: 格式 SMA_sma_{period}
                    const key = `SMA_sma_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('BOLL' === indicatorType) {
                    // BOLL: 格式 BOLL_boll_{period}
                    // 后端返回 period 为 "period,stdDev" (如 "20,2")，取逗号前的纯数字部分
                    const basePeriod = period.split(',')[0];
                    const key = `BOLL_boll_${basePeriod}`;
                    const bollValues = indicator.bollValues || [];
                    result[key] = bollValues
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            upper: normalizeNumber(v.upper),
                            middle: normalizeNumber(v.middle),
                            lower: normalizeNumber(v.lower),
                        }));
                } else if ('MACD' === indicatorType) {
                    // MACD: 格式 MACD_macd_{fast}_{slow}_{signal}
                    const key = `MACD_macd_${period}`;
                    const macdValues = indicator.macdValues || [];
                    result[key] = macdValues
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            diff: normalizeNumber(v.diff),
                            signal: normalizeNumber(v.dea),
                            histogram: normalizeNumber(v.macd),
                        }));
                } else if ('RSI' === indicatorType) {
                    // RSI: 格式 RSI_rsi_{period}
                    const key = `RSI_rsi_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('KDJ' === indicatorType) {
                    // KDJ: 格式 KDJ_kdj_{period}
                    const key = `KDJ_kdj_${period}`;
                    const kdjValues = indicator.kdjValues || [];
                    result[key] = kdjValues
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            k: normalizeNumber(v.k),
                            d: normalizeNumber(v.d),
                            j: normalizeNumber(v.j),
                        }));
                } else if ('CCI' === indicatorType) {
                    // CCI: 格式 CCI_cci_{period}
                    const key = `CCI_cci_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('ATR' === indicatorType) {
                    // ATR: 格式 ATR_atr_{period}
                    const key = `ATR_atr_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('OBV' === indicatorType) {
                    // OBV: 格式 OBV_obv_{period}
                    const key = `OBV_obv_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                } else if ('ADX' === indicatorType) {
                    // ADX: 格式 ADX_adx_{period}
                    const key = `ADX_adx_${period}`;
                    result[key] = values
                        .filter((v: any) => v && v.timestamp !== null && v.timestamp !== undefined)
                        .map((v: any) => ({
                            time: v.timestamp,
                            value: normalizeNumber(v.value),
                        }));
                }
            });
        });

        return result;
    }, [rawIndicators]);

    /**
     * 将 KlineData 转换为 KLineBar 格式
     */
    const convertToKLineBars = (): KLineBar[] => {
        return klineData.map(item => ({
            time: item.timestamp,
            open: item.open,
            high: item.high,
            low: item.low,
            close: item.close,
            volume: item.volume,
        }));
    };

    /**
     * 打开指标配置弹窗
     */
    const handleOpenIndicatorModal = () => {
        setDraftInstances(JSON.parse(JSON.stringify(indicatorInstances)));
        setShowIndicatorModal(true);
    };

    /**
     * 应用指标配置并关闭弹窗
     */
    const handleApplyIndicatorConfig = () => {
        // 深拷贝并对每个实例的 periods 去重
        const deduped: IndicatorConfigMap = {};
        Object.entries(draftInstances).forEach(([type, instances]) => {
            deduped[type] = (instances || []).map(dedupInstancePeriods);
        });
        onIndicatorInstancesChange(deduped);
        setShowIndicatorModal(false);
    };

    /**
     * 取消指标配置，关闭弹窗不保存
     */
    const handleCancelIndicatorConfig = () => {
        setShowIndicatorModal(false);
    };

    /**
     * 切换草稿中某个实例的启用状态
     */
    const handleToggleInstance = (type: string, index: number) => {
        setDraftInstances(prev => {
            const list = [...(prev[type] || [])];
            list[index] = {...list[index], enabled: !list[index].enabled};
            return {...prev, [type]: list};
        });
    };

    /**
     * 更新草稿中某个实例的周期
     */
    const handleInstancePeriodChange = (type: string, instIdx: number, periodIdx: number, value: number | null) => {
        if (null === value) return;
        setDraftInstances(prev => {
            const list = [...(prev[type] || [])];
            const inst = {...list[instIdx]};
            const periods = [...inst.periods];
            periods[periodIdx] = value;
            list[instIdx] = {...inst, periods};
            return {...prev, [type]: list};
        });
    };

    /**
     * 更新草稿中 BOLL 实例的标准差
     */
    const handleInstanceStdDevChange = (type: string, index: number, value: number | null) => {
        if (null === value) return;
        setDraftInstances(prev => {
            const list = [...(prev[type] || [])];
            list[index] = {...list[index], stdDev: value};
            return {...prev, [type]: list};
        });
    };

    /**
     * 添加一个默认实例到指定指标（自动分配颜色）
     */
    const handleAddInstance = (type: string) => {
        const existingColors = (draftInstances[type] || [])
            .filter((i: IndicatorInstanceConfig) => i.color)
            .map((i: IndicatorInstanceConfig) => i.color!);
        const newInst = getDefaultInstanceForType(type);
        newInst.color = getNextIndicatorColor(type, existingColors);
        setDraftInstances(prev => ({
            ...prev,
            [type]: [...(prev[type] || []), newInst],
        }));
    };

    /**
     * 删除指定指标的某个实例
     */
    const handleRemoveInstance = (type: string, index: number) => {
        setDraftInstances(prev => ({
            ...prev,
            [type]: (prev[type] || []).filter((_, i) => i !== index),
        }));
    };

    // 获取最新的K线数据用于OHLC展示
    const latestKline = klineData.length > 0 ? klineData[klineData.length - 1] : null;
    const prevKline = klineData.length > 1 ? klineData[klineData.length - 2] : null;
    const priceChange = latestKline && prevKline ? latestKline.close - prevKline.close : 0;
    const priceChangePercent = prevKline && prevKline.close !== 0 ? (priceChange / prevKline.close) * 100 : 0;

    // 当 OHLC 数据变化时，通知父组件
    useEffect(() => {
        if (latestKline) {
            onOhlcDataChange?.({
                latestKline: {
                    open: latestKline.open,
                    high: latestKline.high,
                    low: latestKline.low,
                    close: latestKline.close,
                    volume: latestKline.volume,
                },
                priceChange,
                priceChangePercent,
            });
        } else {
            onOhlcDataChange?.(null);
        }
    }, [latestKline, priceChange, priceChangePercent, onOhlcDataChange]);

    return (
        <>
            <Card
                size="small"
                className="trading-form-card"
                title={null}
                bodyStyle={{padding: '0', backgroundColor: 'var(--pt-bg-panel)', height: '100%'}}
                style={{marginBottom: '0', border: 'none', height: '100%'}}
            >
                {/* 图表展示区 */}
                {loading ? (
                    <div style={{
                        minHeight: '400px',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--pt-bg-panel)'
                    }}>
                        <Spin size="large" tip={<span style={{color: 'rgba(255,255,255,0.65)'}}>加载K线数据...</span>}/>
                    </div>
                ) : klineData.length > 0 ? (
                    <div style={{
                        minHeight: '400px',
                        height: '100%',
                        padding: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        position: 'relative',
                    }}>
                        <LightweightCandlestickChart
                            key={chartRenderKey}
                            data={convertToKLineBars()}
                            indicators={transformIndicators()}
                            visibleIndicators={visibleIndicators}
                            indicatorColorMap={indicatorColorMap}
                            indicatorPeriodLabels={indicatorPeriodLabels}
                            timeFrame={timeframe}
                            maxVisibleBars={80}
                            markPrice={markPrice}
                            markPriceColor="var(--pt-bear)"
                        />
                    </div>
                ) : (
                    <div style={{
                        minHeight: '400px',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--pt-bg-panel)',
                        color: 'rgba(255,255,255,0.45)',
                    }}>
                        暂无K线数据
                    </div>
                )}
            </Card>

            {/* 技术指标配置弹窗 */}
            <Modal
                title={<span style={{color: 'var(--pt-text-primary)', fontSize: '14px'}}>技术指标配置</span>}
                open={showIndicatorModal}
                onCancel={handleCancelIndicatorConfig}
                footer={null}
                width={500}
                styles={{
                    header: {
                        backgroundColor: 'var(--pt-bg-panel)',
                        borderBottom: '1px solid var(--pt-border)',
                        borderRadius: '8px 8px 0 0'
                    },
                    body: {backgroundColor: 'var(--pt-bg-panel)', padding: '0'},
                    content: {backgroundColor: 'var(--pt-bg-panel)', borderRadius: '8px'},
                }}
                maskClosable={true}
            >
                <div style={{padding: 0}}>
                    {[
                        {section: '主图指标', types: ['EMA', 'SMA', 'BOLL']},
                        {section: '副图指标', types: ['RSI', 'MACD', 'KDJ', 'CCI', 'ATR', 'OBV', 'ADX']},
                    ].map(group => (
                        <div key={group.section}>
                            <div style={{
                                color: 'rgba(255,255,255,0.45)',
                                fontSize: '12px',
                                marginBottom: '8px',
                                marginTop: group.section === '副图指标' ? '16px' : 0
                            }}>{group.section}</div>
                            {group.types.map(type => {
                                const list = draftInstances[type] || [];
                                return (
                                    <div key={type} style={{marginBottom: '12px'}}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span style={{
                                                color: 'var(--pt-text-primary)',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                minWidth: '40px'
                                            }}>{type}</span>
                                            <span onClick={() => handleAddInstance(type)}
                                                  style={{
                                                      color: 'var(--pt-primary)',
                                                      fontSize: '11px',
                                                      cursor: 'pointer'
                                                  }}>+ 添加{type}实例</span>
                                        </div>
                                        {list.length === 0 && <div style={{
                                            color: 'rgba(255,255,255,0.25)',
                                            fontSize: '11px',
                                            marginLeft: '40px'
                                        }}>暂无实例</div>}
                                        {list.map((inst, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                marginTop: '6px'
                                            }}>
                                        <span onClick={() => handleToggleInstance(type, idx)}
                                              style={{
                                                  width: '14px',
                                                  height: '14px',
                                                  border: '1px solid rgba(255,255,255,0.3)',
                                                  borderRadius: '2px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  backgroundColor: inst.enabled ? 'var(--pt-primary)' : 'transparent',
                                                  flexShrink: 0
                                              }}>
                                            {inst.enabled && <span
                                                style={{color: '#000', fontSize: '10px', fontWeight: 'bold'}}>✓</span>}
                                        </span>
                                                {('MACD' === type || inst.periods.length <= 1) &&
                                                    <span title={inst.color || ''} style={{
                                                        width: '10px', height: '10px', borderRadius: '50%',
                                                        backgroundColor: inst.color || '#666', flexShrink: 0,
                                                    }}/>}
                                                {'BOLL' === type ? (
                                                    <>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>周期:</span>
                                                        <InputNumber size="small" min={2} max={200}
                                                                     value={inst.periods[0]}
                                                                     onChange={v => handleInstancePeriodChange(type, idx, 0, v)}
                                                                     disabled={!inst.enabled}
                                                                     style={{
                                                                         width: '60px',
                                                                         backgroundColor: 'var(--pt-bg-hover)',
                                                                         borderColor: 'var(--pt-border)'
                                                                     }}/>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>标准差:</span>
                                                        <InputNumber size="small" min={1} max={5} step={0.5}
                                                                     value={inst.stdDev}
                                                                     onChange={v => handleInstanceStdDevChange(type, idx, v)}
                                                                     disabled={!inst.enabled}
                                                                     style={{
                                                                         width: '58px',
                                                                         backgroundColor: 'var(--pt-bg-hover)',
                                                                         borderColor: 'var(--pt-border)'
                                                                     }}/>
                                                    </>
                                                ) : ('MACD' === type ? (
                                                    <>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>快:</span>
                                                        <InputNumber size="small" min={2} max={100}
                                                                     value={inst.periods[0]}
                                                                     onChange={v => handleInstancePeriodChange(type, idx, 0, v)}
                                                                     disabled={!inst.enabled}
                                                                     style={{
                                                                         width: '55px',
                                                                         backgroundColor: 'var(--pt-bg-hover)',
                                                                         borderColor: 'var(--pt-border)'
                                                                     }}/>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>慢:</span>
                                                        <InputNumber size="small" min={2} max={200}
                                                                     value={inst.periods[1]}
                                                                     onChange={v => handleInstancePeriodChange(type, idx, 1, v)}
                                                                     disabled={!inst.enabled}
                                                                     style={{
                                                                         width: '55px',
                                                                         backgroundColor: 'var(--pt-bg-hover)',
                                                                         borderColor: 'var(--pt-border)'
                                                                     }}/>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>信号:</span>
                                                        <InputNumber size="small" min={2} max={100}
                                                                     value={inst.periods[2]}
                                                                     onChange={v => handleInstancePeriodChange(type, idx, 2, v)}
                                                                     disabled={!inst.enabled}
                                                                     style={{
                                                                         width: '55px',
                                                                         backgroundColor: 'var(--pt-bg-hover)',
                                                                         borderColor: 'var(--pt-border)'
                                                                     }}/>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span style={{
                                                            color: 'rgba(255,255,255,0.45)',
                                                            fontSize: '12px'
                                                        }}>周期:</span>
                                                        {inst.periods.map((p, pi) => {
                                                            const palette = INDICATOR_PALETTES[type] || ['var(--pt-primary)', 'var(--pt-bull)', 'var(--pt-bull)', '#f5a623'];
                                                            const baseIdx = Math.max(0, palette.indexOf(inst.color || '#666') % palette.length);
                                                            const dotColor = palette[(baseIdx + pi) % palette.length];
                                                            return (<React.Fragment key={pi}>
                                                                {inst.periods.length > 1 && <span style={{
                                                                    width: '8px',
                                                                    height: '8px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: dotColor,
                                                                    flexShrink: 0
                                                                }}/>}
                                                                <InputNumber size="small" min={2} max={200} value={p}
                                                                             onChange={v => handleInstancePeriodChange(type, idx, pi, v)}
                                                                             disabled={!inst.enabled}
                                                                             style={{
                                                                                 width: '58px',
                                                                                 backgroundColor: 'var(--pt-bg-hover)',
                                                                                 borderColor: 'var(--pt-border)'
                                                                             }}/>
                                                            </React.Fragment>);
                                                        })}
                                                    </>
                                                ))}
                                                <span onClick={() => handleRemoveInstance(type, idx)}
                                                      style={{
                                                          color: 'rgba(255,255,255,0.3)',
                                                          fontSize: '14px',
                                                          cursor: 'pointer',
                                                          flexShrink: 0
                                                      }}>×</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        borderTop: '1px solid var(--pt-border-subtle)',
                        paddingTop: '12px',
                        marginTop: '8px'
                    }}>
                        <button onClick={handleCancelIndicatorConfig}
                                style={{
                                    padding: '4px 16px',
                                    backgroundColor: 'transparent',
                                    color: 'var(--pt-text-primary)',
                                    border: '1px solid var(--pt-border)',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                }}>取消
                        </button>
                        <button onClick={handleApplyIndicatorConfig}
                                style={{
                                    padding: '4px 16px',
                                    backgroundColor: 'var(--pt-primary)',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: 600
                                }}>应用
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default KlineChartPanel;
